import * as React from 'react'

import { apiJson } from '@/lib/api'
import type { GenerationJob } from '@/types'

interface JobListPayload {
  items: GenerationJob[]
  total: number
}

export interface UseJobsOptions {
  /** Delay for the first poll after a status change. Default 3000ms. */
  initialMs?: number
  /** Cap on the polling delay. Default 30000ms. */
  maxMs?: number
  /** Backoff multiplier applied per consecutive unchanged poll. Default 1.5. */
  factor?: number
  /** Page size for /api/v1/jobs. Default 50. */
  pageSize?: number
  /** Starting page (1-indexed). Default 1. */
  initialPage?: number
  /** Server-side status filter. Passed through to /api/v1/jobs?status=…
   *  Defaults to no filter (all statuses). Pass "completed" so the gallery
   *  doesn't waste its page budget on failed jobs the FE would strip anyway. */
  statusFilter?: string | null
}

const DEFAULT_OPTS: Required<UseJobsOptions> = {
  initialMs: 3000,
  maxMs: 30000,
  factor: 1.5,
  pageSize: 50,
  initialPage: 1,
  statusFilter: null as unknown as string,
}

function _resolveOpts(opts: number | UseJobsOptions | undefined): Required<UseJobsOptions> {
  if (opts == null) return DEFAULT_OPTS
  // Back-compat: accept a single number meaning initialMs.
  if (typeof opts === 'number') return { ...DEFAULT_OPTS, initialMs: opts }
  return { ...DEFAULT_OPTS, ...opts }
}

/**
 * Polls /api/v1/jobs with exponential backoff.
 *
 * - Fingerprint-based reset: any change to the (id, status) tuple of any job
 *   resets the backoff counter to 0 — so a brand-new queued job gets snappy
 *   feedback at `initialMs`, but a long-running OpenRouter image job
 *   (~140s typical) backs off to `maxMs` instead of 28 fast polls.
 * - When no active jobs are present, polls at `maxMs` (slowest cadence).
 *
 * Back-compat: `useJobs(10000)` still works as `useJobs({initialMs: 10000})`.
 */
export function useJobs(opts?: number | UseJobsOptions) {
  const { initialMs, maxMs, factor, pageSize, initialPage, statusFilter } = _resolveOpts(opts)

  const [jobs, setJobs] = React.useState<GenerationJob[]>([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(initialPage)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const pollCountRef = React.useRef(0)
  const fingerprintRef = React.useRef('')

  const load = React.useCallback(async (): Promise<{ hasActive: boolean }> => {
    try {
      const offset = (page - 1) * pageSize
      const statusQs = statusFilter ? `&status=${encodeURIComponent(statusFilter)}` : ''
      const data = await apiJson<JobListPayload>(
        `/api/v1/jobs?limit=${pageSize}&offset=${offset}${statusQs}`,
      )
      const fp = data.items.map((j) => `${j.id}:${j.status}`).join(',')
      if (fp !== fingerprintRef.current) {
        fingerprintRef.current = fp
        pollCountRef.current = 0
      } else {
        pollCountRef.current += 1
      }
      setJobs(data.items)
      setTotal(data.total)
      setError(null)
      const hasActive = data.items.some(
        (j) => j.status === 'queued' || j.status === 'processing',
      )
      return { hasActive }
    } catch (err) {
      setError((err as Error).message)
      return { hasActive: false }
    } finally {
      setLoading(false)
    }
  }, [page, pageSize])

  React.useEffect(() => {
    let cancelled = false
    let timer: number | null = null

    // Reset polling fingerprint whenever the page changes so the new page
    // gets snappy initial fetches instead of inheriting backoff state.
    fingerprintRef.current = ''
    pollCountRef.current = 0

    const tick = async () => {
      if (cancelled) return
      const { hasActive } = await load()
      if (cancelled) return

      // Active jobs: backoff curve from initialMs up to maxMs.
      // Idle: poll at maxMs to catch externally-created jobs reasonably fast
      //       without burning requests when nothing's happening.
      const exp = Math.min(
        initialMs * Math.pow(factor, pollCountRef.current),
        maxMs,
      )
      const delay = hasActive ? exp : maxMs
      timer = window.setTimeout(tick, delay)
    }

    tick()
    return () => {
      cancelled = true
      if (timer != null) window.clearTimeout(timer)
    }
  }, [load, initialMs, maxMs, factor, statusFilter])

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return {
    jobs,
    total,
    page,
    pageSize,
    totalPages,
    setPage,
    loading,
    error,
    refresh: load,
  }
}
